import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  PinInput,
  PinInputField,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { validateTotp } from 'api/mfa';
import { useAuthentication } from 'AuthContext';
import { DASHBOARD_HOME } from 'constants/routes';

const SetupMfa = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { updateUserAndFetchPermissions } = useAuthentication();

  const locationState = location.state || {};
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [qrCode] = useState(locationState.qrCode || '');
  const [totpSecret] = useState(locationState.totpSecret || '');
  const [token] = useState(locationState.token || '');
  const [totp, setTotp] = useState('');
  const [totpError, setTotpError] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpError, setEmailOtpError] = useState('');
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!verified && (!qrCode || !totpSecret || !token)) {
      toast({
        title: 'Invalid MFA Setup',
        description: 'Please start the MFA setup process from the profile menu.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      history.push(DASHBOARD_HOME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidateTotp = async () => {
    try {
      setLoading(true);
      setTotpError('');
      setEmailOtpError('');

      await validateTotp({
        totp,
        email_otp: emailOtp,
        token,
      });

      setLoading(false);
      setVerified(true);

      await updateUserAndFetchPermissions();

      toast({
        title: 'Success',
        description: '2-Factor Authentication has been enabled successfully!',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      history.push(DASHBOARD_HOME);
    } catch (error) {
      setLoading(false);

      const errorCode = error?.response?.data?.error?.code;
      const errorMessage = error?.response?.data?.error?.message || 'Invalid authentication code';

      if (errorCode === 'INVALID_TOTP') {
        setTotpError(errorMessage);
        setEmailOtpError('');
      } else if (errorCode === 'INVALID_EMAIL_OTP') {
        setEmailOtpError(errorMessage);
        setTotpError('');
      } else {
        setTotpError(errorMessage);
        setEmailOtpError('');
      }
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2 && !verified) {
      if (totp.length === 6 && emailOtp.length === 6) {
        handleValidateTotp();
      }
    } else if (verified) {
      const redirectPath = new URLSearchParams(location.search).get('redirect');

      history.push(redirectPath || DASHBOARD_HOME);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
    setTotpError('');
    setEmailOtpError('');
  };

  const handleTotpChange = (value) => {
    setTotp(value);
    setTotpError('');
  };

  const handleEmailOtpChange = (value) => {
    setEmailOtp(value);
    setEmailOtpError('');
  };

  const handleClose = () => {
    history.push(DASHBOARD_HOME);
  };

  if (loading && !qrCode) {
    return (
      <Container maxW='container.md' centerContent py={10}>
        <Text>Loading...</Text>
      </Container>
    );
  }

  return (
    <Container maxW='container.md' centerContent py={10}>
      <VStack spacing={6} align='center'>
        <Heading size='lg'>Setup 2-Factor Authentication</Heading>

        {!verified && <Text fontSize='md' color='gray.600'>{`Step ${currentStep}/2`}</Text>}

        {currentStep === 1 && (
          <VStack spacing={4} align='center'>
            <Text fontSize='md'>Open Google Authenticator app</Text>
            <Text fontSize='md'>Tap &apos;+&apos; and hit &apos;Scan QR code&apos;</Text>
            <Text fontSize='md' maxW='400px' textAlign='center'>
              Scan this QR code to setup 2FA
            </Text>

            {qrCode && (
              <Box borderWidth={1} borderRadius='md' p={4}>
                <Image src={qrCode} alt='QR Code' boxSize='250px' />
              </Box>
            )}

            <Button variant='link' colorScheme='blue' size='sm' onClick={() => setShowSecretModal(true)}>
              Unable to scan QR code?
            </Button>
          </VStack>
        )}

        {currentStep === 2 && !verified && (
          <VStack spacing={6} align='center' width='100%'>
            <Text fontSize='md' maxW='400px' textAlign='center'>
              Enter OTP sent to your email
            </Text>

            <VStack spacing={2} align='center'>
              <HStack>
                <PinInput otp size='lg' value={emailOtp} onChange={handleEmailOtpChange} isInvalid={!!emailOtpError}>
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                </PinInput>
              </HStack>

              {emailOtpError && (
                <Text color='red.500' fontSize='sm'>
                  {emailOtpError}
                </Text>
              )}
            </VStack>

            <Text fontSize='md' maxW='400px' textAlign='center' mt={4}>
              Please enter the authentication code from your authenticator app
            </Text>

            <VStack spacing={2} align='center'>
              <HStack>
                <PinInput otp size='lg' value={totp} onChange={handleTotpChange} isInvalid={!!totpError}>
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                </PinInput>
              </HStack>

              {totpError && (
                <Text color='red.500' fontSize='sm'>
                  {totpError}
                </Text>
              )}
            </VStack>
          </VStack>
        )}

        {verified && (
          <VStack spacing={6} align='center' py={8}>
            <Box fontSize='6xl'>✓</Box>
            <Text fontSize='xl' fontWeight='semibold'>
              2-Factor Authentication is now enabled!
            </Text>
          </VStack>
        )}

        <Flex gap={4}>
          {currentStep === 2 && !verified && (
            <Button variant='outline' onClick={handlePrevStep}>
              Back
            </Button>
          )}

          <Button
            colorScheme='blue'
            onClick={handleNextStep}
            isLoading={loading}
            isDisabled={currentStep === 2 && !verified && (totp.length !== 6 || emailOtp.length !== 6)}
          >
            {currentStep === 2 ? (verified ? 'Close' : 'Verify') : 'Next'}
          </Button>
        </Flex>

        {!verified && (
          <Button variant='ghost' size='sm' onClick={handleClose} mt={4}>
            Cancel
          </Button>
        )}
      </VStack>

      <Modal isOpen={showSecretModal} onClose={() => setShowSecretModal(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Unable to scan QR code</ModalHeader>
          <ModalBody>
            <Text mb={4}>Please enter this key manually in the app to setup 2FA</Text>
            <Box bg='gray.100' p={4} borderRadius='md' fontFamily='mono'>
              {totpSecret}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme='blue'
              onClick={() => {
                setShowSecretModal(false);
                handleNextStep();
              }}
            >
              Next
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default SetupMfa;
